// DriftIntelligence.jsx
import React, { useState, useMemo } from 'react';
import { getSampleByContext } from '../../data/sampleRoachData';
import AILightComponent from "../../../../components/AILightComponent";

const DriftIntelligence = ({ pulseContext = "Overall" }) => {
  const [timeRange, setTimeRange] = useState('14d');
  const [comparisonContext, setComparisonContext] = useState('Overall');
  const [enableAICoach, setEnableAICoach] = useState(false);

  const currentData = useMemo(() => {
    return getSampleByContext(pulseContext === "All" ? "Overall" : pulseContext);
  }, [pulseContext]);

  const comparisonData = useMemo(() => {
    return getSampleByContext(comparisonContext);
  }, [comparisonContext]);

  // Calculate trend (slope)
  const calculateTrend = (data) => {
    if (data.length < 3) return { slope: 0, direction: 'stable', strength: 0 };

    const sorted = [...data].sort((a, b) => new Date(a.roachDate) - new Date(b.roachDate));
    const n = sorted.length;
    const x = Array.from({ length: n }, (_, i) => i);
    const y = sorted.map(r => r.avgScore);

    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);

    let direction = 'stable';
    if (slope > 0.15) direction = 'up';
    else if (slope < -0.15) direction = 'down';

    return {
      slope: parseFloat(slope.toFixed(2)),
      direction,
      strength: Math.min(Math.abs(slope) * 2.5, 1)
    };
  };

  const currentTrend = calculateTrend(currentData);
  const comparisonTrend = calculateTrend(comparisonData);

  // Domain Drift
  const domainDrift = useMemo(() => {
    const currentAvg = currentData.length ? 
      currentData.reduce((sum, r) => sum + r.avgScore, 0) / currentData.length : 0;
    const compAvg = comparisonData.length ? 
      comparisonData.reduce((sum, r) => sum + r.avgScore, 0) / comparisonData.length : 0;

    const diff = parseFloat((currentAvg - compAvg).toFixed(1));

    return {
      diff,
      magnitude: Math.abs(diff),
      isSignificant: Math.abs(diff) > 1.2
    };
  }, [currentData, comparisonData]);

  // Dominant Archetype
  const archetypeShift = useMemo(() => {
    const recent = [...currentData]
      .sort((a, b) => new Date(b.roachDate) - new Date(a.roachDate))
      .slice(0, 5);

    const count = {};
    recent.forEach(r => {
      count[r.type] = (count[r.type] || 0) + 1;
    });

    return Object.entries(count).sort((a, b) => b[1] - a[1])[0]?.[0] || "Balanced Survivor";
  }, [currentData]);

  // Predictive Intelligence Flags
  const predictions = useMemo(() => {
    const flags = [];

    if (currentTrend.direction === 'down' && currentTrend.strength > 0.6) {
      flags.push("⚠️ Recovery speed declining — risk of Numb Mode in next 7 days");
    }
    if (domainDrift.isSignificant && domainDrift.diff < -1) {
      flags.push(`🔄 Chaos spreading from ${comparisonContext} → ${pulseContext} detected`);
    }
    if (currentData.some(r => r.avgScore <= 3)) {
      flags.push("🧊 Low Energy Pulse detected — prioritize recovery rituals");
    }
    if (currentTrend.direction === 'up' && currentTrend.strength > 0.7) {
      flags.push("🚀 Strong upward trajectory — momentum is on your side");
    }

    return flags.length > 0 ? flags : ["🌟 Stable trajectory — keep doing what you're doing"];
  }, [currentTrend, domainDrift, currentData, pulseContext, comparisonContext]);

  // Recent timeline
  const recentRoaches = useMemo(() => {
    return [...currentData]
      .sort((a, b) => new Date(b.roachDate) - new Date(a.roachDate))
      .slice(0, 8);
  }, [currentData]);

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-gray-100 pb-20">
      {/* Header - Tighter on Mobile */}
      <div className="sticky top-0 z-50 bg-[#0a0f1c] border-b border-gray-800 py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-cyan-400 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0">
            🧭
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight">Drift Intelligence</h1>
            <p className="text-gray-300 text-xs">Pattern shifts • Archetypes • Signals</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 lg:px-4 space-y-6 mt-5">
        {/* Trend Cards - Compact */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
          {/* Current Trend */}
          <div className="bg-[#111827] border border-gray-800 rounded-3xl p-4 lg:p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] lg:text-xs uppercase tracking-widest text-gray-300">CURRENT TREND</p>
                <div className="text-4xl lg:text-5xl font-bold mt-2 flex items-center gap-1.5">
                  {currentTrend.direction === 'up' ? '📈' : currentTrend.direction === 'down' ? '📉' : '➡️'}
                  <span className="tabular-nums">{currentTrend.slope > 0 ? '+' : ''}{currentTrend.slope}</span>
                </div>
              </div>
              <div className={`px-3 py-1 text-[10px] lg:text-xs font-semibold rounded-2xl ${currentTrend.direction === 'up' ? 'bg-emerald-500/20 text-emerald-400' : currentTrend.direction === 'down' ? 'bg-rose-500/20 text-rose-400' : 'bg-gray-600/30 text-gray-300'}`}>
                {currentTrend.direction.toUpperCase()}
              </div>
            </div>
            <p className="text-xs lg:text-sm text-gray-300 mt-4">Avg Score Trajectory</p>
          </div>

          {/* Domain Drift */}
          <div className="bg-[#111827] border border-gray-800 rounded-3xl p-4 lg:p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] lg:text-xs uppercase tracking-widest text-gray-300">DOMAIN DRIFT</p>
                <div className="text-2xl lg:text-3xl font-bold mt-2 flex items-center gap-1.5">
                  {domainDrift.diff >= 0 ? '↗️' : '↘️'} {domainDrift.diff}
                </div>
              </div>
              {domainDrift.isSignificant && <span className="text-2xl lg:text-3xl">⚠️</span>}
            </div>
            <p className="text-xs lg:text-sm text-gray-300 mt-4">
              vs <span className="text-purple-400">{comparisonContext}</span>
            </p>
          </div>

          {/* Archetype */}
          <div className="bg-[#111827] border border-gray-800 rounded-3xl p-4 lg:p-6">
            <p className="text-[10px] lg:text-xs uppercase tracking-widest text-gray-300">DOMINANT ARCHETYPE</p>
            <div className="text-lg lg:text-xl font-semibold text-purple-300 mt-3 leading-tight">
              {archetypeShift}
            </div>
            <p className="text-[10px] text-gray-300 mt-6">LAST 5 PULSES</p>
          </div>
        </div>

        {/* Intelligence Flags - Tighter */}
        <div className="bg-gradient-to-br from-[#1a2338] to-[#111827] border border-amber-900/40 rounded-3xl p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🧠</span>
            <h3 className="text-sm lg:text-lg font-semibold">Intelligence Flags</h3>
          </div>
          <div className="space-y-3">
            {predictions.map((flag, i) => (
              <div key={i} className="bg-black/40 border-l-4 border-amber-500 rounded-2xl p-4 text-gray-200 text-sm leading-snug">
                {flag}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Pulse Timeline */}
        <div className="bg-[#111827] border border-gray-800 rounded-3xl p-4 lg:p-6">
          <h3 className="font-semibold text-sm lg:text-lg mb-4 flex items-center gap-2">
            📅 Recent History
          </h3>

          <div className="space-y-3">
            {recentRoaches.map((roach) => {
              const isHigh = roach.avgScore >= 7;
              const isLow = roach.avgScore <= 4;

              return (
                <div 
                  key={roach._id} 
                  className="flex items-center gap-3 bg-[#0a0f1c] hover:bg-[#1a2338] p-3.5 lg:p-4 rounded-2xl transition-all border border-transparent hover:border-gray-700"
                >
                  <div className="w-14 text-right font-mono text-xs text-gray-300">
                    {new Date(roach.roachDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>

                  <div className="flex-1 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${isHigh ? 'bg-emerald-500/20' : isLow ? 'bg-rose-500/20' : 'bg-cyan-500/20'}`}>
                      {isHigh ? '⚡' : isLow ? '🧊' : '🪳'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{roach.type}</div>
                      <div className="text-[10px] text-gray-300">Day {roach.dayNumber}</div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className={`text-3xl lg:text-4xl font-bold tabular-nums ${isHigh ? 'text-emerald-400' : isLow ? 'text-rose-400' : 'text-cyan-400'}`}>
                      {roach.avgScore}
                    </div>
                    <div className="text-[10px] text-gray-300 -mt-1">AVG</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cross Domain */}
        <div className="bg-[#111827] border border-gray-800 rounded-3xl p-4 lg:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
            <h3 className="font-semibold text-sm lg:text-lg">Cross-Domain Comparison</h3>
            <select
              value={comparisonContext}
              onChange={(e) => setComparisonContext(e.target.value)}
              className="bg-[#0f172a] border border-gray-700 rounded-xl px-4 py-1 text-sm focus:outline-none focus:border-purple-500 w-full lg:w-auto"
            >
              {['Overall', 'Home', 'Work', 'Campus', 'Learning', 'Social', 'Environment'].map(ctx => (
                <option key={ctx} value={ctx}>{ctx}</option>
              ))}
            </select>
          </div>
          <p className="text-xs lg:text-sm text-gray-300">
            Comparing <span className="text-purple-400">{pulseContext}</span> with other domains
          </p>
        </div>
      </div>
      {/* ── AI Drift Coach ── */}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => setEnableAICoach(p => !p)}
          className={`w-fit px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
            enableAICoach
              ? "bg-purple-500 text-white"
              : "bg-[#111827] text-purple-300 border border-purple-500/30"
          }`}
        >
          {enableAICoach ? "🤖 AI Drift Coach Active" : "🧠 Ask AI Drift Coach"}
        </button>

        {enableAICoach && (
          <div className="w-full bg-[#111827] rounded-3xl border border-purple-500/20 p-3">
            <AILightComponent
              mode="daily_life_mood_dashboard"
              param1={`Drift Intelligence Analysis — ${pulseContext}`}
              param2={`
                Context Being Analyzed: ${pulseContext}
                Compared Against: ${comparisonContext}

                Trend Analysis:
                - Direction: ${currentTrend.direction.toUpperCase()}
                - Slope: ${currentTrend.slope > 0 ? '+' : ''}${currentTrend.slope}
                - Strength: ${(currentTrend.strength * 100).toFixed(0)}%

                Comparison Context Trend:
                - Direction: ${comparisonTrend.direction.toUpperCase()}
                - Slope: ${comparisonTrend.slope > 0 ? '+' : ''}${comparisonTrend.slope}

                Domain Drift:
                - Difference vs ${comparisonContext}: ${domainDrift.diff > 0 ? '+' : ''}${domainDrift.diff}
                - Significant Drift: ${domainDrift.isSignificant ? 'YES' : 'NO'}

                Dominant Archetype (last 5 pulses): ${archetypeShift}

                Intelligence Flags:
                ${predictions.map(f => `- ${f}`).join('\n')}

                Recent 8 Pulses:
                ${recentRoaches.map(r =>
                  `- ${new Date(r.roachDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} | Score: ${r.avgScore} | Type: ${r.type} | Day ${r.dayNumber}`
                ).join('\n')}

                Please analyze:
                - What is the core drift pattern?
                - Is this context bleeding into others?
                - What archetype shift is happening?
                - What are the top 3 recovery or momentum actions?
                - Any burnout or plateau risk in next 7 days?
              `}
              selectedLanguageName="English"
              wordLimit={500}
              outputFormat="structured"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DriftIntelligence;